import { useState, useCallback } from "react";
import { literals } from "@/constants/literals";
import { useNavigate } from "react-router-dom";
import { useLoader } from "@/store/useLoader";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import ExitsOrderInfo from "./components/ExitsOrderInfo";
import useExitsDataStore from "@/store/useExitsData.store";

import { askOrderExist, getPackagingOrderInfo } from "@/lib/services/order";
import { appToast } from "@/lib/toast";
import { EXITS_INITAL_STATE } from "@/constants/constants";
import ExitsOrderHeader from "../components/ExitsOrderHeader";
import GetInfoInputs from "./components/GetInfoInputs";
import type { ExitsOrderData } from "@/types/types.d";

export type ExitsSearchData = {
  document9r1: string;
  step: string;
};

const CreateExits = () => {
  const [searchingData, setSearchingData] = useState<ExitsSearchData>({
    document9r1: "",
    step: "",
  });
  const [exitsGeneralData, setExitsGeneralData] =
    useState<ExitsOrderData>(EXITS_INITAL_STATE);
  const [needToChange, setNeedToChange] = useState(false);
  const [lastDocument9R1, setLastDocument9R1] = useState("");
  const [lastStep, setLastStep] = useState("");
  const [shownOrderInfo, setShownOrderInfo] = useState(false);
  const [failValidation, setFailValidation] = useState(false);

  const navigate = useNavigate();
  const { upLoader, downLoader } = useLoader();
  const { updateExtisData } = useExitsDataStore();

  const changeSearchingData = (property: string, value: string) => {
    if (failValidation) setFailValidation(false);
    const _areEqueals = () => {
      if (property === "step")
        return (
          lastStep.length > 0 &&
          value === lastStep &&
          searchingData.document9r1 === lastDocument9R1
        );
      if (property === "document9r1")
        return (
          lastDocument9R1.length === 10 &&
          value === lastDocument9R1 &&
          searchingData.step === lastStep
        );
      return false;
    };
    if (_areEqueals()) {
      if (needToChange) setNeedToChange(false);
      if (!shownOrderInfo) {
        setShownOrderInfo(true);
      }
    }
    if (!_areEqueals() && shownOrderInfo) {
      if (property === "step") setShownOrderInfo(false);
      if (property === "document9r1" && value !== lastDocument9R1)
        setNeedToChange(true);
    }
    setSearchingData((prev) => ({ ...prev, [property]: value }));
  };

  const getOrderInfo = useCallback(async () => {
    if (
      searchingData.document9r1.length !== 10 ||
      searchingData.step.length === 0
    )
      return;
    try {
      upLoader();
      if (needToChange) setNeedToChange(false);
      const askedData = await getPackagingOrderInfo(
        searchingData.step,
        searchingData.document9r1,
      );
      setExitsGeneralData({
        area: literals.exits,
        ...searchingData,
        ...askedData,
      });
      setLastDocument9R1(searchingData.document9r1);
      setLastStep(searchingData.step);
      setShownOrderInfo(true);
      downLoader();
    } catch (error) {
      if (
        error === literals.errorNotFound ||
        (error instanceof Error && error.message === literals.notFound)
      ) {
        appToast.warning(literals.noData, literals.noDataInOrder);
        setShownOrderInfo(true);
      } else {
        appToast.error(literals.aProblem, literals.getNecesaryInfoError);
      }
      setExitsGeneralData({
        area: literals.exits,
        ...searchingData,
      });
      downLoader();
    }
  }, [downLoader, upLoader, searchingData, needToChange]);

  const confirmCanContinue = useCallback(async () => {
    try {
      upLoader();
      const orderInfo = await askOrderExist(exitsGeneralData);
      const { restrict, message } = orderInfo;
      downLoader();
      if (restrict) {
        appToast.warning(literals.noValidData, message);
        setFailValidation(true);
        return false;
      }
      return true;
    } catch (error) {
      downLoader();
      setFailValidation(true);
      if (error) {
        appToast.error(literals.aProblem, literals.getNecesaryInfoError);
        return false;
      }
      return false;
    }
  }, [downLoader, upLoader, exitsGeneralData]);

  const continueCapture = useCallback(
    async (
      event: React.BaseSyntheticEvent<
        Event,
        EventTarget & HTMLFormElement,
        EventTarget
      >,
    ) => {
      event.preventDefault();
      if (!shownOrderInfo || needToChange) return;
      const canContinue = await confirmCanContinue();
      if (!canContinue) return;
      updateExtisData(exitsGeneralData);
      navigate("new-group");
    },
    [
      shownOrderInfo,
      needToChange,
      confirmCanContinue,
      navigate,
      exitsGeneralData,
      updateExtisData,
    ],
  );

  return (
    <div className="min-h-fulldpx flex flex-col justify-between bg-ib-pt-color-white">
      <ExitsOrderHeader newOrderText />
      <form
        onSubmit={continueCapture}
        className="flex flex-col flex-1 justify-between w-full md:justify-start"
      >
        <div className="w-full px-4 md:px-14 lg:px-[calc((100%-797px)/2)] pt-4 md:flex md:flex-row md:gap-x-8 flex-wrap md:min-h-64">
          <GetInfoInputs
            exitsData={searchingData}
            changeData={changeSearchingData}
            searchAgainMsg={needToChange}
          />
          {shownOrderInfo ? (
            <ExitsOrderInfo orderInfo={exitsGeneralData} />
          ) : null}
        </div>
        <div className="py-4 flex flex-col gap-4 border-t border-ib-pt-color-gray-300 md:mx-14 md:items-center lg:mx-[calc((100%-797px)/2)]">
          <div className="px-4 md:capture-button">
            <PrimaryButton
              size="medium"
              type={shownOrderInfo && !needToChange ? "submit" : "button"}
              label={literals.continue}
              colour="red"
              disabled={
                searchingData.step.length === 0 ||
                searchingData.document9r1.length !== 10 ||
                failValidation
              }
              onClick={(event) => {
                if (shownOrderInfo && !needToChange) {
                  return;
                }
                event.preventDefault();
                void getOrderInfo();
              }}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateExits;
